import { theme } from '@components-shared/src/stitches';
import type { BarCoordinatesArgs, Coordinate } from '../paintCanvas';

const MIN_BAR_HEIGHT = 24;

function barCoordinates(args: BarCoordinatesArgs): Coordinate {
  const {
    index,
    barWidth,
    minBarHeight = MIN_BAR_HEIGHT,
    maxBarHeight,
    barMargin,
    amplitude,
    containerHeight,
  } = args;
  const currBarHeight = Math.round((amplitude / 100) * maxBarHeight) + minBarHeight;
  const verticalCenter = Math.round((containerHeight - currBarHeight) / 2);

  return {
    x: index * (barWidth + barMargin),
    y: verticalCenter + containerHeight / 2,
    w: barWidth,
    h: currBarHeight,
  };
}

type PaintCanvasArgs = {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  normalizedPeaks: number[];
  maxBarHeight: number;
  barWidth: number;
  barMargin: number;
  claimedBars: Set<number>;
  hoveredBars: Set<number>;
  clickedBars: Set<number>;
  /** If user can overwrite claimed bars, then the claimed bars are painted with a lighter color */
  canOverwrite: boolean;
};

export function paintCanvas(args: PaintCanvasArgs) {
  const {
    canvasRef,
    normalizedPeaks,
    maxBarHeight,
    barWidth,
    barMargin,
    claimedBars,
    hoveredBars,
    clickedBars,
    canOverwrite,
  } = args;

  const ref = canvasRef.current;
  const ctx = ref?.getContext('2d');
  if (!ref || !ctx) {
    return;
  }

  // erase the canvas before painting or waveforms will stack every update
  const bgColor = '#F3F4F6';
  ctx.fillStyle = bgColor;
  ctx.clearRect(0, 0, ref.width, ref.height);

  ctx.beginPath();
  normalizedPeaks.forEach((p, i) => {
    const coord = barCoordinates({
      index: i,
      barWidth,
      barMargin,
      maxBarHeight,
      amplitude: p,
      containerHeight: ref.height,
    });

    // paint top half of waveform
    roundRect({
      ctx,
      x: coord.x,
      y: coord.y,
      w: coord.w,
      h: coord.h / 2,
      r: 5,
    });

    const isClaimed = claimedBars.has(i);
    const isHovered = hoveredBars.has(i);
    const isClicked = clickedBars.has(i);

    fillBar({
      ctx,
      barWidth,
      maxBarHeight,
      isClaimed,
      isHovered,
      isClicked,
      canOverwrite,
    });
  });
  ctx.closePath();
}

type RoundRectArgs = {
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
};

function roundRect(args: RoundRectArgs) {
  const { ctx, x, y, w, h } = args;
  let { r } = args;

  if (w < 2 * r) r = w / 2;
  if (h < 2 * r) r = h / 2;

  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  return ctx;
}

type BarFillArgs = {
  ctx: CanvasRenderingContext2D;
  barWidth: number;
  maxBarHeight: number;
  isClaimed: boolean;
  isHovered: boolean;
  isClicked: boolean;
  canOverwrite: boolean;
};
function fillBar(args: BarFillArgs) {
  const { ctx, isClaimed, isHovered, isClicked, canOverwrite } = args;

  if (canOverwrite) {
    if (isClicked) {
      ctx.fillStyle = theme.colors.accent700.value;
    } else if (isHovered) {
      ctx.fillStyle = theme.colors.brand100.value;
    } else if (isClaimed) {
      ctx.fillStyle = theme.colors.neutral400.value;
    } else {
      ctx.fillStyle = theme.colors.neutral300.value;
    }
    return ctx.fill();
  }

  if (isClicked) {
    ctx.fillStyle = theme.colors.accent700.value;
  } else if (isClaimed) {
    ctx.fillStyle = theme.colors.neutral900.value;
  } else if (isHovered) {
    ctx.fillStyle = theme.colors.neutral400.value;
  } else {
    ctx.fillStyle = theme.colors.neutral300.value;
  }
  return ctx.fill();
}
